import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { DocumentType } from '@repo/shared/dtos/document'
import { GenerationCacheOptions } from '@repo/shared/events/generation'
import { DataStatus, findLastChatSessionId } from '@repo/ui/utils'

import { AsyncThunkConfig, getCaseWorkerFilter, RejectValue, storeActions } from '@/store/actions'
import { Beneficiary } from '@/store/beneficiary'
import { ChatMessage, ChatSession } from '@/store/chat'
import { Document } from '@/store/document'
import { getCurrentBeneficiary } from '@/store/helpers.ts'
import { Task } from '@/store/task'

const document = {
  create: createAsyncThunk<
    { document: Document },
    { beneficiaryId?: string } & (
      | { file: File }
      | {
          content: string
          type:
            | DocumentType.NOTE
            | DocumentType.NOTE_FROM_FILE_ANALYSIS
            | DocumentType.NOTE_FROM_EMAIL_ANALYSIS
        }
    ),
    AsyncThunkConfig
  >('CREATE_DOCUMENT', async (payload, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { api } = extra
    const beneficiaryId = payload.beneficiaryId || getState().beneficiary.currentBeneficiaryId
    if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    if ('file' in payload) {
      // Upload
      const document = await api.documents.uploadOne({
        beneficiaryId,
        organizationId,
        file: payload.file,
      })
      if (hasError(document)) return rejectWithValue(handleError(document))
      return fulfillWithValue({ document })
    } else {
      // Create
      const document = await api.documents.createNote({
        beneficiaryId,
        organizationId,
        fields: { content: payload.content, type: payload.type },
      })
      if (hasError(document)) return rejectWithValue(handleError(document))
      return fulfillWithValue({ document })
    }
  }),

  update: createAsyncThunk<
    { document: Document },
    { id: string; content: string } | { id: string; title: string },
    AsyncThunkConfig
  >('UPDATE_DOCUMENT', async (fields, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { api } = extra
    const beneficiaryId = getState().beneficiary.currentBeneficiaryId
    if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const document = await api.documents.updateOne({
      beneficiaryId,
      organizationId,
      fields,
    })
    if (hasError(document)) return rejectWithValue(handleError(document))
    return fulfillWithValue({ document })
  }),

  delete: createAsyncThunk<{ isDeleted: boolean }, { document: Document }, AsyncThunkConfig>(
    'DELETE_DOCUMENT',
    async ({ document }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
      const { api } = extra
      const beneficiaryId = getState().beneficiary.currentBeneficiaryId
      if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
      const organizationId = getState().user.currentOrganizationId
      if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

      const isDeleted = await api.documents.deleteOne({
        beneficiaryId,
        documentId: document.id,
        organizationId,
      })
      if (hasError(isDeleted)) return rejectWithValue(handleError(isDeleted))
      return fulfillWithValue({ isDeleted })
    },
  ),

  restore: createAsyncThunk<
    { document: Document; notify?: boolean },
    { documentId: string; notify?: boolean },
    AsyncThunkConfig
  >(
    'RESTORE_DOCUMENT',
    async (
      { documentId, notify = true },
      { extra, fulfillWithValue, rejectWithValue, getState },
    ) => {
      const { api } = extra
      const beneficiaryId = getState().beneficiary.currentBeneficiaryId
      if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
      const organizationId = getState().user.currentOrganizationId
      if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

      const document = await api.documents.restoreOne({
        beneficiaryId,
        organizationId,
        documentId,
      })
      if (hasError(document)) return rejectWithValue(handleError(document))
      return fulfillWithValue({ document, notify })
    },
  ),
}

const chatSession = {
  initChatSession: createAsyncThunk<
    { chatMessages: ChatMessage[] },
    { chatSessionId: string },
    AsyncThunkConfig
  >(
    'INIT_CHAT_SESSION',
    async ({ chatSessionId }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
      const beneficiaryId = getState().beneficiary.currentBeneficiaryId
      if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)

      const { api } = extra
      const data = await api.chatSessions.getInitialValuesFromOne({
        chatSessionId,
      })
      if (hasError(data)) return rejectWithValue(handleError(data))
      return fulfillWithValue(data)
    },
  ),
  create: createAsyncThunk<
    { chatSession: ChatSession },
    { beneficiaryId: string },
    AsyncThunkConfig
  >(
    'CREATE_CHAT_SESSION',
    async ({ beneficiaryId }, { extra, fulfillWithValue, rejectWithValue, dispatch }) => {
      const { api } = extra
      const chatSession = await api.chatSessions.createOne({
        beneficiaryId,
      })
      if (hasError(chatSession)) return rejectWithValue(handleError(chatSession))

      await dispatch(
        storeActions.beneficiary.chatSession.initChatSession({
          chatSessionId: chatSession.id,
        }),
      )
      return fulfillWithValue({ chatSession })
    },
  ),
  selectLastChatSession: createAsyncThunk<{ chatSessionId: string }, void, AsyncThunkConfig>(
    'SELECT_LAST_CHAT_SESSION',
    (_, { rejectWithValue, getState, fulfillWithValue }) => {
      const chatSessions = getState().chat.sessions
      if (!DataStatus.isFulfilled(chatSessions)) return rejectWithValue(RejectValue.NoChatSessions)
      const lastChatSessionId = findLastChatSessionId(chatSessions)
      return fulfillWithValue({
        chatSessionId: lastChatSessionId || 'disabled',
      })
    },
  ),
}

export const beneficiary = {
  app: {
    chatSession,

    create: createAsyncThunk<
      { beneficiary: Beneficiary },
      { beneficiaryFields: Pick<Beneficiary, 'fullName' | 'profile'> },
      AsyncThunkConfig
    >(
      'CREATE_BENEFICIARY',
      async (
        { beneficiaryFields },
        { extra, fulfillWithValue, rejectWithValue, getState, dispatch },
      ) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const beneficiary = await api.beneficiaries.one.create({
          beneficiaryFields,
          organizationId,
        })
        if (hasError(beneficiary)) return rejectWithValue(handleError(beneficiary))

        // Create a note with the beneficiary profile
        void dispatch(
          storeActions.beneficiary.document.create({
            beneficiaryId: beneficiary.id,
            content: beneficiaryFields.profile.value,
            type: DocumentType.NOTE_FROM_FILE_ANALYSIS,
          }),
        )
        return fulfillWithValue({ beneficiary })
      },
    ),

    delete: createAsyncThunk<{ isDeleted: boolean }, { beneficiaryId: string }, AsyncThunkConfig>(
      'DELETE_BENEFICIARY',
      async ({ beneficiaryId }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const res = await altApi.beneficiaries.deleteOne({ beneficiaryId, organizationId })
        if (hasError(res)) return rejectWithValue(handleError(res))
        return fulfillWithValue({ isDeleted: res.success })
      },
    ),

    initBeneficiary: createAsyncThunk<
      {
        chatSessions: ChatSession[]
        documents: Document[]
        tasks: Task[]
        currentBeneficiary: Beneficiary
      },
      { beneficiaryId: string; isSameBeneficiary: boolean },
      AsyncThunkConfig
    >(
      'INIT_BENEFICIARY',
      async ({ beneficiaryId }, { extra, getState, fulfillWithValue, rejectWithValue }) => {
        const { api, altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        let chatSessions = getState().chat.sessions // cache
        let documents = getState().document.all // cache
        let tasks = getState().task.current // cache
        let currentBeneficiary = getCurrentBeneficiary(getState())
        if (
          // Not in cache
          !DataStatus.isFulfilled(chatSessions) ||
          !DataStatus.isFulfilled(documents) ||
          !DataStatus.isFulfilled(tasks)
        ) {
          const data = await api.beneficiaries.one.getInitialValues({
            beneficiaryId,
            organizationId,
          })
          if (hasError(data)) return rejectWithValue(handleError(data))
          const tasksRes = await altApi.tasks.getAll({ beneficiaryId, organizationId })
          if (hasError(tasksRes)) return rejectWithValue(handleError(tasksRes))
          chatSessions = data.chatSessions
          documents = data.documents
          tasks = tasksRes
        }
        if (!DataStatus.isFulfilled(currentBeneficiary)) {
          const caseWorker = getState().user.caseWorker
          if (!DataStatus.isFulfilled(caseWorker)) return rejectWithValue(RejectValue.NoCaseWorker)
          const data = await api.beneficiaries.one.get({
            asCaseWorkerId: getCaseWorkerFilter({
              targetCaseWorkerId: caseWorker.id,
              caseWorker,
            }),
            beneficiaryId,
            organizationId,
          })
          if (hasError(data)) return rejectWithValue(handleError(data))
          currentBeneficiary = data
        }
        return fulfillWithValue({
          chatSessions,
          documents,
          tasks,
          currentBeneficiary,
        })
      },
    ),

    update: createAsyncThunk<
      { beneficiary: Beneficiary },
      { beneficiaryFields: Partial<Pick<Beneficiary, 'fullName'>> },
      AsyncThunkConfig
    >(
      'UPDATE_BENEFICIARY',
      async ({ beneficiaryFields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { api } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const beneficiary = await api.beneficiaries.one.patch({
          beneficiaryFields,
          beneficiaryId,
          organizationId,
        })
        if (hasError(beneficiary)) return rejectWithValue(handleError(beneficiary))
        return fulfillWithValue({ beneficiary })
      },
    ),

    refreshProfile: createAsyncThunk<
      { beneficiary: Beneficiary },
      { beneficiaryId: string } & GenerationCacheOptions,
      AsyncThunkConfig
    >(
      'REFRESH_BENEFICIARY_PROFILE',
      async (
        { beneficiaryId, cacheOptions },
        { extra, rejectWithValue, fulfillWithValue, getState },
      ) => {
        const { api } = extra
        const state = getState()
        const generationId = state.assistant.generationId
        if (!generationId) return rejectWithValue(RejectValue.NoGenerationId)
        const organizationId = state.user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
        const caseWorker = state.user.caseWorker
        if (!DataStatus.isFulfilled(caseWorker)) return rejectWithValue(RejectValue.NoCaseWorker)
        const user = state.user.me
        if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)

        const res = await api.beneficiaries.one.refreshProfile({
          beneficiaryId,
          cacheOptions,
          generationId,
          organizationId,
          userId: user.id,
        })
        if (hasError(res)) return rejectWithValue(handleError(res))

        const beneficiary = await api.beneficiaries.one.get({
          asCaseWorkerId: getCaseWorkerFilter({
            targetCaseWorkerId: caseWorker.id,
            caseWorker,
          }),
          beneficiaryId,
          organizationId,
        })
        if (hasError(beneficiary)) return rejectWithValue(handleError(beneficiary))
        return fulfillWithValue({ beneficiary })
      },
    ),

    refreshStructuredData: createAsyncThunk<
      { beneficiary: Beneficiary },
      { beneficiaryId: string } & GenerationCacheOptions,
      AsyncThunkConfig
    >(
      'REFRESH_BENEFICIARY_STRUCTURED_DATA',
      async (
        { beneficiaryId, cacheOptions },
        { extra, rejectWithValue, fulfillWithValue, getState },
      ) => {
        const { api } = extra
        const state = getState()
        const generationId = state.assistant.generationId
        if (!generationId) return rejectWithValue(RejectValue.NoGenerationId)
        const organizationId = state.user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
        const user = state.user.me
        if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)
        const caseWorker = state.user.caseWorker
        if (!DataStatus.isFulfilled(caseWorker)) return rejectWithValue(RejectValue.NoCaseWorker)

        const res = await api.beneficiaries.one.refreshStructuredData({
          beneficiaryId,
          cacheOptions,
          generationId,
          organizationId,
          userId: user.id,
        })
        if (hasError(res)) return rejectWithValue(handleError(res))

        const beneficiary = await api.beneficiaries.one.get({
          asCaseWorkerId: getCaseWorkerFilter({
            targetCaseWorkerId: caseWorker.id,
            caseWorker,
          }),
          beneficiaryId,
          organizationId,
        })
        if (hasError(beneficiary)) return rejectWithValue(handleError(beneficiary))
        return fulfillWithValue({ beneficiary })
      },
    ),

    document,
  },
}
